import {posthog} from 'posthog-js';

/**
 * Creates an API client with authentication and error handling
 * @param {string} accessToken - The bearer token for authentication
 * @param {Function} refreshTokenCallback - Optional callback to refresh token on 401 errors
 * @returns {Object} - API client methods
 */
export const createApiClient = (accessToken, refreshTokenCallback = null) => {
    const baseUrl = import.meta.env.VITE_API_URL;

    if (!baseUrl) {
        console.error('VITE_API_URL environment variable is not defined');
    }

    /**
     * Sanitizes sensitive data for logging
     * @param {Object} data - The data to sanitize
     * @returns {Object} - Sanitized data
     */
    const sanitizeForLogging = (data) => {
        if (!data || typeof data !== 'object') return data;

        const sensitiveKeys = ['password', 'token', 'authorization', 'auth', 'key', 'secret'];
        const sanitized = {...data};

        Object.keys(sanitized).forEach(key => {
            const lowerKey = key.toLowerCase();
            if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
                sanitized[key] = '[REDACTED]';
            }
        });

        return sanitized;
    };

    /**
     * Sanitizes headers for logging
     * @param {Headers} headers - The headers object
     * @returns {Object} - Sanitized headers object
     */
    const sanitizeHeaders = (headers) => {
        const sanitized = {};
        if (headers) {
            headers.forEach((value, key) => {
                const lowerKey = key.toLowerCase();
                if (lowerKey.includes('authorization') || lowerKey.includes('token')) {
                    sanitized[key] = '[REDACTED]';
                } else {
                    sanitized[key] = value;
                }
            });
        }
        return sanitized;
    };

    /**
     * Captures API errors to PostHog with comprehensive context
     * @param {Error} error - The error object
     * @param {Object} context - Additional context about the request
     */
    const captureApiError = (error, context) => {
        try {
            const errorData = {
                error_message: error.message,
                error_name: error.name,
                error_stack: error.stack,
                api_endpoint: context.endpoint,
                api_method: context.method,
                api_url: context.url,
                status_code: context.statusCode,
                request_headers: sanitizeHeaders(context.requestHeaders),
                response_headers: sanitizeHeaders(context.responseHeaders),
                has_access_token: !!accessToken,
                base_url: baseUrl,
                timestamp: new Date().toISOString(),
                user_agent: navigator.userAgent,
                // Add request body info if available (sanitized)
                ...(context.requestBody && {
                    request_body_type: context.requestBody instanceof FormData ? 'FormData' : 'JSON',
                    request_body_size: context.requestBody instanceof FormData
                        ? '[FormData object]'
                        : JSON.stringify(context.requestBody).length
                }),
                // Add response info if available
                ...(context.responseText && {
                    response_preview: context.responseText.substring(0, 500) // First 500 chars
                })
            };

            // Capture the error to PostHog
            posthog.capture('api_error', {
                ...errorData,
                $groups: {
                    api_endpoint: context.endpoint,
                    error_type: context.statusCode ? 'http_error' : 'network_error'
                }
            });

            // Also capture as an exception for better error tracking
            posthog.captureException(error, {
                tags: {
                    api_endpoint: context.endpoint,
                    api_method: context.method,
                    status_code: context.statusCode
                },
                extra: errorData
            });
        } catch (loggingError) {
            console.error('Failed to capture API error to PostHog:', loggingError);
        }
    };

    /**
     * Builds a URL with query parameters
     * @param {string} endpoint - The base endpoint
     * @param {Object} params - Query parameters to append
     * @returns {string} - Complete URL with query string
     */
    const buildUrlWithParams = (endpoint, params) => {
        const url = `${baseUrl}${endpoint}`;

        if (!params || Object.keys(params).length === 0) {
            return url;
        }

        // Filter out undefined/null values and build query string
        const filteredParams = Object.entries(params)
            .filter(([key, value]) => value !== undefined && value !== null && value !== '')
            .reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});

        if (Object.keys(filteredParams).length === 0) {
            return url;
        }

        const queryString = new URLSearchParams(filteredParams).toString();
        return `${url}?${queryString}`;
    };

    /**
     * Makes an HTTP request to the API
     * @param {string} endpoint - The API endpoint to call
     * @param {Object} options - Request options
     * @returns {Promise<any>} - Response data
     */
    const request = async (endpoint, options = {}) => {
        // Extract params from options for GET requests
        const {params, ...fetchOptions} = options;

        // Build URL with query parameters if this is a GET request and params exist
        const url = (fetchOptions.method === 'GET' && params)
            ? buildUrlWithParams(endpoint, params)
            : `${baseUrl}${endpoint}`;

        const headers = {
            ...fetchOptions.headers,
        };

        // Only set Content-Type to application/json if body is not FormData
        const isFormData = fetchOptions.body instanceof FormData;
        if (!isFormData && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }

        // Add authorization header if access token is provided
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const config = {
            ...fetchOptions,
            headers,
        };

        // Prepare context for error logging
        const errorContext = {
            endpoint,
            method: fetchOptions.method || 'GET',
            url,
            requestHeaders: new Headers(headers),
            requestBody: fetchOptions.body
        };

        try {
            const response = await fetch(url, config);

            // Add response info to error context
            errorContext.statusCode = response.status;
            errorContext.responseHeaders = response.headers;

            // Handle HTTP errors
            if (!response.ok) {
                let errorMessage = `API error: ${response.status}`;
                let responseText = '';

                try {
                    const errorData = await response.json();
                    responseText = JSON.stringify(errorData);

                    // Handle different error response formats
                    if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    } else if (errorData.detail) {
                        // Handle FastAPI validation errors (array format)
                        if (Array.isArray(errorData.detail)) {
                            const messages = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err));
                            errorMessage = messages.join(', ');
                        } else if (typeof errorData.detail === 'string') {
                            errorMessage = errorData.detail;
                        } else {
                            errorMessage = JSON.stringify(errorData.detail);
                        }
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (Array.isArray(errorData)) {
                        const messages = errorData.map(err => err.msg || err.message || JSON.stringify(err));
                        errorMessage = messages.join(', ');
                    } else {
                        // If it's an object, try to extract meaningful info
                        errorMessage = JSON.stringify(errorData);
                    }
                } catch (error) {
                    try {
                        responseText = await response.text();
                        errorMessage = responseText;
                    } catch (textError) {
                        errorMessage = `API error: ${response.status} ${response.statusText}`;
                    }
                }

                // Add response text to context
                errorContext.responseText = responseText;

                // Handle 401 Unauthorized - trigger token refresh if callback is provided
                if (response.status === 401 && refreshTokenCallback) {
                    console.log('Received 401 error, triggering immediate token refresh...');
                    try {
                        await refreshTokenCallback();
                        // After refresh, throw a specific error that components can catch to retry
                        const refreshError = new Error('TOKEN_REFRESH_TRIGGERED');
                        refreshError.name = 'TokenRefreshError';
                        refreshError.status = 401;
                        throw refreshError;
                    } catch (refreshError) {
                        console.error('Token refresh failed:', refreshError);
                        // If refresh fails, continue with the original 401 error
                    }
                }

                const apiError = new Error(errorMessage);
                apiError.name = 'ApiError';
                apiError.status = response.status;
                apiError.statusText = response.statusText;

                // Capture the HTTP error (skip if it's a 401 that triggered refresh)
                if (!(response.status === 401 && refreshTokenCallback)) {
                    captureApiError(apiError, errorContext);
                }

                throw apiError;
            }

            // Handle 204 No Content responses
            if (response.status === 204) {
                return null; // No content to parse
            }

            // Check if the response is empty
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }

            // Handle blob responses (for file downloads)
            if (fetchOptions.responseType === 'blob') {
                return {
                    data: await response.blob(),
                    headers: response.headers
                };
            }

            return await response.text();
        } catch (error) {
            console.error('API request failed:', error);

            // Only capture to PostHog if this isn't already an ApiError (to avoid double-logging)
            if (error.name !== 'ApiError') {
                // This is likely a network error, fetch error, or other non-HTTP error
                error.name = error.name || 'NetworkError';
                captureApiError(error, errorContext);
            }

            throw error;
        }
    };

    return {
        /**
         * Makes a GET request
         * @param {string} endpoint - The endpoint to call
         * @param {Object} params - Query parameters to include in URL
         * @param {Object} options - Additional fetch options
         * @returns {Promise<any>} - Response data
         */
        get: (endpoint, params = {}, options = {}) => {
            return request(endpoint, {
                method: 'GET',
                params,
                ...options
            });
        },

        /**
         * Makes a POST request
         * @param {string} endpoint - The endpoint to call
         * @param {Object|FormData} data - The data to send
         * @param {Object} options - Additional options
         * @returns {Promise<any>} - Response data
         */
        post: (endpoint, data, options = {}) => {
            const body = data instanceof FormData ? data : JSON.stringify(data);
            return request(endpoint, {
                method: 'POST',
                body,
                ...options
            });
        },

        /**
         * Makes a PUT request
         * @param {string} endpoint - The endpoint to call
         * @param {Object|FormData} data - The data to send
         * @param {Object} options - Additional options
         * @returns {Promise<any>} - Response data
         */
        put: (endpoint, data, options = {}) => {
            const body = data instanceof FormData ? data : JSON.stringify(data);
            return request(endpoint, {
                method: 'PUT',
                body,
                ...options
            });
        },

        /**
         * Makes a PATCH request
         * @param {string} endpoint - The endpoint to call
         * @param {Object|FormData} data - The data to send
         * @param {Object} options - Additional options
         * @returns {Promise<any>} - Response data
         */
        patch: (endpoint, data, options = {}) => {
            const body = data instanceof FormData ? data : JSON.stringify(data);
            return request(endpoint, {
                method: 'PATCH',
                body,
                ...options
            });
        },

        /**
         * Makes a DELETE request
         * @param {string} endpoint - The endpoint to call
         * @param {Object} options - Additional options
         * @returns {Promise<any>} - Response data
         */
        delete: (endpoint, options = {}) => {
            return request(endpoint, {method: 'DELETE', ...options});
        }
    };
};